<script lang="ts">
  import BookOpen from '@lucide/svelte/icons/book-open';
  import FileText from '@lucide/svelte/icons/file-text';
  import Video from '@lucide/svelte/icons/video';
  import Users from '@lucide/svelte/icons/users';
  import CalendarDays from '@lucide/svelte/icons/calendar-days';
  import MessageSquare from '@lucide/svelte/icons/message-square';
  import ChevronDown from '@lucide/svelte/icons/chevron-down';
  import ChevronRight from '@lucide/svelte/icons/chevron-right';
  import Check from '@lucide/svelte/icons/check';
  import Plus from '@lucide/svelte/icons/plus';
  import PanelLeft from '@lucide/svelte/icons/panel-left';
  import GraduationCap from '@lucide/svelte/icons/graduation-cap';
  import Settings from '@lucide/svelte/icons/settings';
  import Globe from '@lucide/svelte/icons/globe';
  import ClipboardCheck from '@lucide/svelte/icons/clipboard-check';

  let { view = 'courses', compact = false }: { view?: string; compact?: boolean } = $props();
  const headings: Record<string, string> = {
    courses: 'Content',
    live: 'Content',
    batches: 'Batches',
    assessment: 'Submissions',
    community: 'Community',
    website: 'Institution website'
  };
  const courseItems = [
    { name: 'Welcome to the course', description: 'Lesson · Introduction', icon: BookOpen, colour: 'blue' },
    { name: 'Understanding functions', description: 'Lesson · Video and notes', icon: Video, colour: 'blue' },
    { name: 'Functions & graphs', description: 'Lesson · Reading material', icon: FileText, colour: 'blue' },
    {
      name: 'Put your knowledge into practice',
      description: 'Exercise · 5 questions',
      icon: ClipboardCheck,
      colour: 'green'
    }
  ];
  const students = [
    { initials: 'AM', name: 'Aarav Mehta', email: 'aarav@example.com', status: 'Present' },
    { initials: 'KR', name: 'Kavya Rao', email: 'kavya@example.com', status: 'Present' },
    { initials: 'ZK', name: 'Zoya Khan', email: 'zoya@example.com', status: 'Late' },
    { initials: 'AS', name: 'Arjun Shah', email: 'arjun@example.com', status: 'Present' }
  ];
</script>

<div class="workspace" class:compact aria-label={`Edvello ${headings[view] ?? 'course'} interface with sample data`}>
  <aside class="app-sidebar">
    <div class="institution">
      <span class="institution-icon"><GraduationCap size={18} /></span>
      <div><strong>Northfield Academy</strong><small>Institution workspace</small></div>
      <ChevronDown size={12} />
    </div>
    <div class="sidebar-group">
      <span class="sidebar-back"><ChevronRight size={12} /> Courses</span><strong class="course-name"
        >Mathematics, made clear</strong
      ><span class="course-type">Self-paced course</span>
    </div>
    <div class="app-menu">
      <span><MessageSquare size={15} /> News Feed</span>
      <span class:chosen={view === 'courses' || view === 'live'}><BookOpen size={15} /> Content <small>4</small></span>
      {#if view === 'courses' || view === 'live'}<div class="nested">
          <span><ChevronDown size={12} /> Getting started</span><span class="nested-item"
            ><BookOpen size={12} /> Welcome to the course</span
          ><span class="nested-item"><Video size={12} /> Understanding functions</span>
        </div>{/if}
      <span class:chosen={view === 'batches'}><CalendarDays size={15} /> Batches</span>
      <span class:chosen={view === 'assessment'}><ClipboardCheck size={15} /> Submissions</span>
      <span><Users size={15} /> People</span>
      <span><GraduationCap size={15} /> Certificates</span>
      <span class:chosen={view === 'community'}><MessageSquare size={15} /> Community</span>
      <span class:chosen={view === 'website'}><Globe size={15} /> Institution website</span>
    </div>
    <div class="sidebar-foot"><Settings size={15} /> Settings <span class="user-avatar">MI</span></div>
  </aside>
  <div class="app-main">
    <div class="app-topbar">
      <PanelLeft size={15} /><span class="topbar-rule"></span><strong>{headings[view]}</strong><span
        class="topbar-spacer"
      ></span><span class="published"><span></span> Published</span><span class="user-avatar">MI</span>
    </div>
    <div class="app-content">
      {#if view === 'courses'}
        <div class="content-heading">
          <div>
            <h3>Content</h3>
          </div>
          <span class="app-primary"><Plus size={12} /> Add content</span>
        </div>
        <div class="course-counts">
          <div><small><BookOpen size={12} /> Sections</small><strong>2</strong></div>
          <div><small><FileText size={12} /> Lessons</small><strong>3</strong></div>
          <div><small><ClipboardCheck size={12} /> Exercises</small><strong>1</strong></div>
        </div>
        <div class="content-section">
          <div class="section-bar">
            <span><ChevronDown size={13} /> Getting started <small>3 lessons</small></span><Plus size={14} />
          </div>
          {#each courseItems.slice(0, 3) as item}<div class="lesson-row">
              <span class="lesson-icon"><item.icon size={16} /></span>
              <div><strong>{item.name}</strong><small>{item.description}</small></div>
              <ChevronRight size={13} />
            </div>{/each}
        </div>
        <div class="content-section practice">
          <div class="section-bar">
            <span><ChevronDown size={13} /> A little practice <small>1 exercise</small></span><Plus size={14} />
          </div>
          <div class="lesson-row">
            <span class="lesson-icon green"><ClipboardCheck size={16} /></span>
            <div><strong>Put your knowledge into practice</strong><small>Exercise · 5 questions</small></div>
            <ChevronRight size={13} />
          </div>
        </div>
      {:else if view === 'live'}
        <div class="content-heading">
          <div>
            <h3>Functions & graphs</h3>
            <p>Live class · Mathematics</p>
          </div>
          <span class="outline-action">English <ChevronDown size={12} /></span>
        </div>
        <div class="live-card">
          <span class="live-icon"><Video size={25} /></span><span class="session-type">Live session</span>
          <h4>Live Session</h4>
          <p>Functions & graphs · Google Meet</p>
          <div class="session-meta">
            <span><CalendarDays size={14} /> Nov 4, 2026, 4:00 PM IST</span><span>Starts in 2h 15m</span>
          </div>
          <span class="app-primary">Add to calendar <ChevronDown size={12} /></span><span class="calendar-link"
            >Copy link</span
          >
        </div>
        <div class="session-material">
          <FileText size={18} />
          <div>
            <strong>Before the session</strong>
            <p>Review the lesson notes and bring your questions.</p>
          </div>
        </div>
      {:else if view === 'batches'}
        <div class="content-heading">
          <div>
            <h3>Grade 10 · Mathematics</h3>
            <p>Apr 1, 2026 to Mar 31, 2027 | Asia/Kolkata</p>
          </div>
          <span class="outline-action">Edit batch</span>
        </div>
        <div class="batch-tabs">
          <span>Overview</span><span>People</span><span>Courses</span><span>Schedule</span><span class="selected"
            >Attendance</span
          ><span>Materials</span><span>Notices</span>
        </div>
        <div class="attendance-heading">
          <div>
            <h4>Attendance</h4>
            <p>Wednesday, 4 November · Mathematics</p>
          </div>
          <span class="outline-action"><Check size={12} /> Mark all present</span>
        </div>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Student</th><th>Status</th></tr></thead><tbody
              >{#each students as student}<tr
                  ><td
                    ><span class="student-cell"
                      ><span class="student-avatar">{student.initials}</span><span
                        ><strong>{student.name}</strong><small>{student.email}</small></span
                      ></span
                    ></td
                  ><td
                    ><span class="attendance-status" class:late={student.status === 'Late'}
                      >{student.status}<ChevronDown size={12} /></span
                    ></td
                  ></tr
                >{/each}</tbody
            >
          </table>
        </div>
        <div class="table-footer"><span>4 of 4 marked</span><span class="app-primary">Save attendance</span></div>
      {:else if view === 'assessment'}
        <div class="content-heading">
          <div>
            <h3>Submissions</h3>
            <p>Review your students’ work and share feedback.</p>
          </div>
          <span class="outline-action">All exercises <ChevronDown size={12} /></span>
        </div>
        <div class="submission">
          <div class="submission-person">
            <span class="student-avatar">KR</span>
            <div><strong>Kavya Rao</strong><small>Functions & graphs · Submitted</small></div>
            <span class="review-status">Submitted</span>
          </div>
          <h4>Explain how to find the gradient of a line.</h4>
          <div class="answer">
            <small>Student response</small>
            <p>
              The gradient is the change in y divided by the change in x. For the points (1, 2) and (3, 6), the gradient
              is (6 − 2) / (3 − 1) = 2.
            </p>
          </div>
          <div class="feedback">
            <strong>Add comment</strong>
            <p>Clear explanation, Kavya. You have shown each step of the calculation.</p>
          </div>
          <div class="review-footer">
            <span>Grade <strong>5 / 5</strong></span><span class="app-primary">Submit Grades</span>
          </div>
        </div>
      {:else if view === 'community'}
        <div class="content-heading">
          <div>
            <h3>Community</h3>
            <p>A place for questions and shared understanding.</p>
          </div>
          <span class="app-primary"><Plus size={12} /> Ask Community</span>
        </div>
        <div class="community-filter">Find Question <span>All <ChevronDown size={12} /></span></div>
        {#each [['KR', 'How do we know whether a relation is a function?', 'Kavya Rao', '3 replies', '8'], ['AM', 'A useful way to remember the power rule', 'Aarav Mehta', '5 replies', '12'], ['MI', 'This week’s practice questions are ready', 'Meera Iyer', '2 replies', '6']] as question}<div
            class="question-row"
          >
            <div class="vote"><ChevronRight size={12} /><strong>{question[4]}</strong></div>
            <div>
              <h4>{question[1]}</h4>
              <p>{question[2]} asked recently <span>#Mathematics</span></p>
              <small><MessageSquare size={12} /> {question[3].split(' ')[0]}</small>
            </div>
          </div>{/each}
      {:else}
        <div class="content-heading">
          <div>
            <h3>Institution website</h3>
            <p>Your identity. Your courses. Your own space.</p>
          </div>
          <span class="app-primary">View website <Globe size={12} /></span>
        </div>
        <div class="site-preview">
          <div class="site-preview-nav">
            <strong><GraduationCap size={18} /> Northfield Academy</strong><span
              >Courses <span class="site-signin">Login</span></span
            >
          </div>
          <div class="site-preview-hero">
            <span class="academy-mark"><GraduationCap size={36} /></span>
            <h4>A little curiosity.<br />A world of possibility.</h4>
            <p>Thoughtful courses for your next chapter.</p>
            <span class="academy-button">Start Learning</span>
          </div>
          <div class="site-course-list">
            <span><BookOpen size={18} /> Mathematics</span><span><FileText size={18} /> Physics</span>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .workspace {
    display: flex;
    min-height: 520px;
    width: 100%;
    color: #242833;
    background: #fff;
    font-family: Geist, sans-serif;
    font-size: 12px;
    line-height: 1.5;
    text-align: left;
    border-radius: 12px;
    overflow: hidden;
  }
  .app-sidebar {
    width: 214px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    padding: 20px 12px 12px;
    background: #fafafa;
    border-right: 1px solid #e8e9ee;
  }
  .institution {
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 0 3px 28px;
  }
  .institution-icon {
    display: grid;
    place-items: center;
    background: #e9edff;
    color: #1447e6;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;
  }
  .institution strong {
    display: block;
    font-size: 10px;
  }
  .institution small {
    display: block;
    color: #737784;
    font-size: 9px;
  }
  .institution > :global(svg:last-child) {
    margin-left: auto;
  }
  .sidebar-group {
    padding: 0 8px 18px;
  }
  .sidebar-back {
    color: #1447e6;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
  }
  .sidebar-back :global(svg) {
    rotate: 180deg;
  }
  .course-name {
    display: block;
    font-size: 12px;
    margin-top: 11px;
  }
  .course-type {
    display: block;
    color: #737784;
    font-size: 10px;
    margin-top: 3px;
  }
  .app-menu {
    display: grid;
    gap: 3px;
  }
  .app-menu > span {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 10px;
    border-radius: 6px;
    color: #555b69;
    font-size: 11px;
  }
  .app-menu > span.chosen {
    background: #e9edfc;
    color: #1447e6;
    font-weight: 550;
  }
  .app-menu small {
    margin-left: auto;
    font-size: 9px;
  }
  .nested {
    margin: 2px 0 9px 17px;
    border-left: 1px solid #e4e6ed;
    padding-left: 9px;
    color: #616574;
    font-size: 9px;
    display: grid;
    gap: 12px;
  }
  .nested span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .nested-item {
    padding-left: 7px;
    font-size: 8px;
  }
  .sidebar-foot {
    display: flex;
    align-items: center;
    gap: 10px;
    border-top: 1px solid #e8e9ee;
    padding: 16px 8px 0;
    margin-top: auto;
    color: #626876;
    font-size: 10px;
  }
  .user-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-grid;
    place-items: center;
    background: #e8ecf5;
    color: #42516f;
    font-size: 8px;
    font-weight: 600;
  }
  .sidebar-foot .user-avatar {
    margin-left: auto;
  }
  .app-main {
    flex: 1;
    min-width: 0;
  }
  .app-topbar {
    height: 47px;
    display: flex;
    align-items: center;
    gap: 13px;
    padding-inline: 22px;
    border-bottom: 1px solid #e8e9ee;
    font-size: 10px;
  }
  .topbar-rule {
    height: 14px;
    width: 1px;
    background: #e8e9ee;
  }
  .topbar-spacer {
    flex: 1;
  }
  .published {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;
    color: #4e5869;
  }
  .published > span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #24966b;
  }
  .app-content {
    padding: 28px 30px;
  }
  .content-heading {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    align-items: center;
    margin-bottom: 22px;
  }
  h3 {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.025em;
    margin: 0 0 4px;
  }
  p {
    margin: 0;
    color: #69707f;
    font-size: 10px;
  }
  .app-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 5px;
    padding: 8px 11px;
    background: #1447e6;
    color: white;
    font-size: 9px;
    white-space: nowrap;
    font-weight: 500;
  }
  .course-counts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 22px;
  }
  .course-counts > div {
    border: 1px solid #e4e6ed;
    padding: 11px 14px;
    border-radius: 7px;
  }
  .course-counts small {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #707582;
    font-size: 9px;
  }
  .course-counts strong {
    display: block;
    font-size: 22px;
    font-weight: 550;
    margin-top: 4px;
    line-height: 1.1;
  }
  .content-section {
    border: 1px solid #e6e7ed;
    border-radius: 7px;
    overflow: hidden;
  }
  .practice {
    margin-top: 14px;
  }
  .section-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f6f7fa;
    padding: 10px 13px;
    font-size: 10px;
  }
  .section-bar > span {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .section-bar small {
    font-size: 8px;
    color: #707582;
    margin-left: 5px;
  }
  .lesson-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px;
    border-top: 1px solid #eceef3;
  }
  .lesson-icon {
    width: 30px;
    height: 30px;
    background: #edf2ff;
    border-radius: 6px;
    color: #1447e6;
    display: grid;
    place-items: center;
  }
  .lesson-icon.green {
    background: #ecf7f0;
    color: #26855d;
  }
  .lesson-row strong {
    font-size: 10px;
    font-weight: 500;
    display: block;
  }
  .lesson-row small {
    font-size: 8px;
    color: #757b88;
    display: block;
    margin-top: 3px;
  }
  .lesson-row > :global(svg:last-child) {
    color: #8d94a1;
  }
  .outline-action {
    border: 1px solid #e1e4ec;
    border-radius: 5px;
    padding: 7px 9px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 9px;
    white-space: nowrap;
  }
  .live-card {
    text-align: center;
    border: 1px solid #e1e5f0;
    border-radius: 10px;
    padding: 28px 20px;
  }
  .live-icon {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 14px;
    background: #eef2ff;
    color: #1447e6;
    margin: 0 auto 12px;
  }
  .session-type {
    font-size: 9px;
    color: #1447e6;
  }
  h4 {
    margin: 7px 0;
    font-size: 16px;
    font-weight: 550;
    letter-spacing: -0.02em;
  }
  .live-card h4 {
    font-size: 23px;
  }
  .session-meta {
    display: flex;
    justify-content: center;
    gap: 17px;
    margin: 17px 0 20px;
    font-size: 9px;
    color: #565f71;
  }
  .session-meta span {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .calendar-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 9px;
    margin-top: 12px;
    color: #63708b;
  }
  .session-material {
    display: flex;
    gap: 13px;
    align-items: center;
    padding: 17px;
    margin-top: 15px;
    background: #f7f8fb;
    border-radius: 7px;
    color: #687da6;
  }
  .session-material strong {
    font-size: 10px;
    color: #303747;
  }
  .session-material p {
    margin-top: 3px;
  }
  .batch-tabs {
    display: flex;
    gap: 13px;
    padding-bottom: 11px;
    border-bottom: 1px solid #e6e8ee;
    font-size: 9px;
    white-space: nowrap;
    color: #70788a;
    overflow: hidden;
  }
  .batch-tabs .selected {
    color: #1447e6;
    font-weight: 600;
  }
  .attendance-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 17px 0;
  }
  .attendance-heading h4 {
    font-size: 15px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
  }
  th {
    text-align: left;
    font-size: 9px;
    font-weight: 500;
    color: #70788a;
    background: #f8f9fc;
    padding: 10px 12px;
  }
  th:last-child {
    width: 32%;
  }
  td {
    border-bottom: 1px solid #edf0f5;
    padding: 11px 12px;
  }
  .student-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .student-avatar {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #eaf0fb;
    color: #48618b;
    font-size: 9px;
  }
  .student-cell strong {
    font-size: 10px;
    font-weight: 500;
  }
  .student-cell small {
    display: block;
    font-size: 8px;
    color: #657187;
  }
  .attendance-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    border: 1px solid #dce9e2;
    border-radius: 4px;
    color: #267154;
    font-size: 9px;
  }
  .attendance-status.late {
    color: #906116;
    border-color: #ebdfc7;
  }
  .table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    font-size: 9px;
    color: #657187;
  }
  .submission {
    border: 1px solid #e6e8ef;
    border-radius: 8px;
    padding: 20px;
  }
  .submission-person {
    display: flex;
    align-items: center;
    gap: 9px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf0f5;
    margin-bottom: 18px;
  }
  .submission-person strong {
    font-size: 11px;
  }
  .submission-person small {
    display: block;
    font-size: 9px;
    color: #737b8b;
  }
  .review-status {
    margin-left: auto;
    font-size: 9px;
    border-radius: 4px;
    padding: 4px 8px;
    background: #fff3d8;
    color: #855c12;
  }
  .submission h4 {
    font-size: 15px;
  }
  .answer {
    background: #f6f8fc;
    padding: 14px;
    border-radius: 6px;
    margin-top: 15px;
  }
  .answer small {
    font-size: 8px;
    color: #737b8b;
  }
  .answer p {
    margin-top: 6px;
    line-height: 1.8;
    color: #404c61;
    font-size: 11px;
  }
  .feedback {
    padding: 14px;
    border: 1px solid #e6e8ef;
    border-radius: 6px;
    margin-top: 15px;
  }
  .feedback strong {
    font-size: 9px;
  }
  .feedback p {
    margin-top: 5px;
    line-height: 1.7;
  }
  .review-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 17px;
    color: #69758b;
    font-size: 10px;
  }
  .review-footer strong {
    color: #1447e6;
    margin-left: 7px;
  }
  .community-filter {
    display: flex;
    justify-content: space-between;
    border: 1px solid #e6e8ef;
    border-radius: 6px;
    padding: 10px 13px;
    color: #737b8b;
    font-size: 10px;
    margin-bottom: 10px;
  }
  .community-filter > span {
    display: flex;
    align-items: center;
    gap: 9px;
  }
  .question-row {
    display: flex;
    gap: 15px;
    padding: 20px 0;
    border-bottom: 1px solid #edf0f5;
  }
  .vote {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    border: 1px solid #e5e8f1;
    border-radius: 5px;
    width: 30px;
    padding: 5px;
    color: #1447e6;
    flex-shrink: 0;
    align-self: start;
  }
  .vote :global(svg) {
    rotate: -90deg;
  }
  .question-row h4 {
    font-size: 12px;
    margin: 0 0 7px;
  }
  .question-row p {
    font-size: 9px;
  }
  .question-row p span {
    margin-left: 12px;
    color: #1447e6;
  }
  .question-row small {
    display: flex;
    gap: 4px;
    align-items: center;
    color: #657187;
    font-size: 8px;
    margin-top: 10px;
  }
  .site-preview {
    border: 1px solid #e5e8ed;
    border-radius: 7px;
    overflow: hidden;
  }
  .site-preview-nav {
    padding: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 8px;
    background: #fcfcfa;
  }
  .site-preview-nav strong {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #2f514c;
  }
  .site-signin {
    margin-left: 15px;
  }
  .site-preview-hero {
    padding: 24px 20px;
    text-align: center;
    background: #eef4ef;
  }
  .academy-mark {
    color: #3c665b;
    display: inline-block;
  }
  .site-preview-hero h4 {
    font-family: 'Instrument Serif', Georgia, serif;
    font-size: 37px;
    line-height: 1.05;
    color: #254c43;
    margin-block: 10px;
  }
  .site-preview-hero p {
    color: #527366;
    font-size: 9px;
  }
  .academy-button {
    display: inline-block;
    background: #315e50;
    color: #fff;
    padding: 7px 12px;
    border-radius: 4px;
    font-size: 8px;
    margin-top: 16px;
  }
  .site-course-list {
    display: flex;
    gap: 20px;
    padding: 20px;
    color: #365a50;
    font-size: 10px;
  }
  .site-course-list > span {
    display: flex;
    gap: 9px;
    align-items: center;
  }
  .compact .app-sidebar {
    width: 175px;
  }
  .compact .app-content {
    padding: 24px;
  }
  @media (max-width: 760px) {
    .workspace {
      min-height: 465px;
    }
    .app-sidebar {
      display: none;
    }
    .app-topbar {
      padding-inline: 17px;
    }
    .app-content,
    .compact .app-content {
      padding: 20px 16px;
    }
    .content-heading {
      margin-bottom: 18px;
    }
    h3 {
      font-size: 19px;
    }
    .content-heading p {
      max-width: 210px;
      font-size: 9px;
    }
    .content-heading > .app-primary {
      padding: 7px;
      font-size: 8px;
    }
    .course-counts {
      gap: 7px;
      margin-bottom: 17px;
    }
    .course-counts > div {
      padding: 10px;
    }
    .lesson-row {
      gap: 8px;
      padding: 12px 9px;
    }
    .lesson-row strong {
      font-size: 9px;
    }
    .lesson-row small {
      font-size: 8px;
    }
    .lesson-row > :global(svg:last-child) {
      margin-left: auto;
    }
    .section-bar small {
      display: none;
    }
    .live-card {
      padding: 22px 12px;
    }
    .live-card h4 {
      font-size: 22px;
    }
    .session-meta {
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }
    .compact .live-card {
      padding: 12px;
    }
    .compact .live-icon {
      width: 36px;
      height: 36px;
      margin-bottom: 4px;
    }
    .compact .live-card h4 {
      font-size: 18px;
      line-height: 1.2;
    }
    .compact .session-meta {
      margin: 8px 0 10px;
      gap: 5px;
    }
    .batch-tabs {
      gap: 15px;
      overflow-x: auto;
      padding-block: 5px 12px;
    }
    .attendance-heading > .outline-action {
      display: none;
    }
    .student-cell small {
      display: none;
    }
    .attendance-heading p {
      font-size: 9px;
    }
    .attendance-status {
      min-width: 80px;
    }
    .submission {
      padding: 13px;
    }
    .submission h4 {
      font-size: 13px;
    }
    .question-row h4 {
      font-size: 11px;
    }
    .question-row p span {
      display: block;
      margin-left: 0;
      margin-top: 4px;
    }
    .site-preview-hero h4 {
      font-size: 33px;
    }
    .site-preview-nav {
      gap: 10px;
      font-size: 7px;
    }
  }
</style>
